import { Info, Minus, Plus } from "lucide-react";
import React, { useState } from "react";

import {
  IgBorderColors,
  IgCoreBackgroundColors,
  IgCoreTextColors,
  IgIconColors,
} from "@intelligems/ig-design-tokens";
import { Box, ButtonBase, Stack, SxProps, Theme } from "@mui/material";

import { IgTooltip } from "./IgTooltip";
import { IgTypography } from "./IgTypography";

export interface IgNumericalCounterProps {
  value?: number;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  label?: string;
  tooltipMessage?: string;
  onChange: (value: number | undefined) => void;
  sx?: SxProps<Theme>;
}

const neutralBg = IgCoreBackgroundColors.interactive.neutral;

const getBorder = (disabled: boolean) =>
  `1px solid ${IgBorderColors.core[disabled ? "disabled" : "default"]}`;

const StepperButton = ({
  onClick,
  disabled,
  side,
}: {
  onClick: () => void;
  disabled: boolean;
  side: "left" | "right";
}) => (
  <ButtonBase
    disabled={disabled}
    onClick={onClick}
    aria-label={side === "left" ? "Decrease value" : "Increase value"}
    sx={{
      width: 40,
      height: 40,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      border: getBorder(disabled),
      backgroundColor: disabled ? neutralBg.disabled : neutralBg.default,
      borderRadius: side === "left" ? "6px 0 0 6px" : "0 6px 6px 0",
      "&:hover": disabled ? {} : { backgroundColor: neutralBg.hover },
    }}
  >
    {side === "left" ? (
      <Minus
        size={16}
        color={disabled ? IgCoreTextColors.placeholder : IgIconColors.light}
      />
    ) : (
      <Plus
        size={16}
        color={disabled ? IgCoreTextColors.placeholder : IgIconColors.light}
      />
    )}
  </ButtonBase>
);

export const IgNumericalCounter = ({
  value,
  min,
  max,
  step = 1,
  disabled = false,
  label,
  tooltipMessage,
  onChange,
  sx,
}: IgNumericalCounterProps) => {
  const [isFocused, setIsFocused] = useState(false);

  const isAtMin = min !== undefined && value !== undefined && value <= min;
  const isAtMax = max !== undefined && value !== undefined && value >= max;

  const counter = (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        borderRadius: "6px",
        overflow: "hidden",
        ...(isFocused &&
          !disabled && {
            outline: `2px solid ${IgBorderColors.accent.active}`,
            outlineOffset: -2,
          }),
        ...sx,
      }}
    >
      <StepperButton
        onClick={() => onChange((value ?? min ?? 0) - step)}
        disabled={disabled || isAtMin}
        side="left"
      />

      <Box
        component="input"
        type="number"
        value={value ?? ""}
        disabled={disabled}
        step={step}
        min={min}
        max={max}
        onChange={(ev: React.ChangeEvent<HTMLInputElement>) =>
          onChange(ev.target.value === "" ? undefined : Number(ev.target.value))
        }
        onBlur={() => {
          setIsFocused(false);
          if (value === undefined) return;
          if (min !== undefined && value < min) onChange(min);
          else if (max !== undefined && value > max) onChange(max);
        }}
        onFocus={() => setIsFocused(true)}
        sx={{
          width: 60,
          height: 40,
          border: "none",
          borderTop: getBorder(disabled),
          borderBottom: getBorder(disabled),
          backgroundColor: disabled
            ? neutralBg.disabled
            : IgCoreBackgroundColors.baseLightHigh,
          color: disabled
            ? IgCoreTextColors.placeholder
            : IgCoreTextColors.primary,
          textAlign: "center",
          fontSize: "14px",
          lineHeight: 1.5,
          outline: "none",
          padding: "0 12px",
          fontFamily: "inherit",
          "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
            WebkitAppearance: "none",
            margin: 0,
          },
          MozAppearance: "textfield",
        }}
      />

      <StepperButton
        onClick={() => onChange((value ?? min ?? 0) + step)}
        disabled={disabled || isAtMax}
        side="right"
      />
    </Box>
  );

  if (label) {
    return (
      <Stack direction="row" spacing={5} alignItems="center">
        <Stack direction="row" spacing={1} alignItems="center">
          <IgTypography>{label}</IgTypography>
          {tooltipMessage && (
            <IgTooltip title={tooltipMessage}>
              <Info size={16} />
            </IgTooltip>
          )}
        </Stack>
        {counter}
      </Stack>
    );
  }

  return counter;
};
